import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { upcomingEvents, parseEventDate } from "@/lib/events";

const PAGE_KEYS = { "/": "home", "/contact": "contact", "/events": "events" };

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"][data-seo]`);
  if (!href) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    el.dataset.seo = "1";
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

const absolute = (base, path) => (path?.startsWith("http") ? path : `${base}${path?.startsWith("/") ? "" : "/"}${path || ""}`);

export function Seo() {
  const { pathname } = useLocation();
  const { content } = useSite();
  const seo = content.seo;

  useEffect(() => {
    if (pathname.startsWith("/admin")) {
      document.title = `Site Admin — ${seo.site_name}`;
      upsertMeta("name", "robots", "noindex, nofollow");
      return;
    }
    const key = PAGE_KEYS[pathname];
    const page = (key && seo.pages?.[key]) || {};
    const base = (seo.site_url || window.location.origin).replace(/\/$/, "");
    const rawTitle = page.title || seo.default_title;
    const title = key === "home" || !key || rawTitle.includes(seo.site_name) ? rawTitle : `${rawTitle}${seo.title_suffix || ""}`;
    const description = page.description || seo.default_description;
    const url = `${base}${pathname === "/" ? "/" : pathname}`;
    const image = absolute(base, seo.og_image || "/og-image.jpg");

    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", seo.keywords);
    upsertMeta("name", "robots", seo.robots_index === false ? "noindex, nofollow" : "index, follow");
    upsertMeta("name", "google-site-verification", seo.google_site_verification);
    upsertMeta("name", "msvalidate.01", seo.bing_site_verification);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", seo.site_name);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:locale", "en_CA");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);
    upsertLink("canonical", url);

    const c = content.contact;
    const [street = "", city = ""] = (c.address || "").split(",").map((s) => s.trim());
    upsertJsonLd("ld-org", {
      "@context": "https://schema.org",
      "@type": ["Organization", "LocalBusiness"],
      name: seo.site_name,
      alternateName: "UBCJA Local 675",
      url: base,
      logo: absolute(base, content.logo_url),
      image,
      description: seo.default_description,
      telephone: c.phone,
      email: c.email,
      address: { "@type": "PostalAddress", streetAddress: street, addressLocality: city, addressRegion: "ON", addressCountry: "CA" },
      openingHours: c.hours,
      parentOrganization: { "@type": "Organization", name: "United Brotherhood of Carpenters and Joiners of America" },
    });

    if (key === "events") {
      const events = upcomingEvents(content.events).slice(0, 10).map((e) => {
        const when = parseEventDate(e.date);
        return {
          "@type": "Event",
          name: e.title,
          startDate: when ? when.toISOString().slice(0, 10) : undefined,
          location: e.location ? { "@type": "Place", name: e.location, address: c.address } : undefined,
          organizer: { "@type": "Organization", name: seo.site_name, url: base },
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          eventStatus: "https://schema.org/EventScheduled",
        };
      });
      upsertJsonLd("ld-events", { "@context": "https://schema.org", "@graph": events });
    } else {
      document.getElementById("ld-events")?.remove();
    }
  }, [pathname, content, seo]);

  return null;
}
